import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE } from "@/lib/config";

/**
 * Componente SEO reutilizable.
 *
 * Props:
 *  - title:     título de la pestaña (se compone con "· ASATTA" salvo raíz)
 *  - description
 *  - path:      ruta canónica (por ejemplo "/directorio"). Se combina con SITE_URL.
 *  - image:     URL absoluta para Open Graph / Twitter
 *  - type:      "website" | "article" (default: "website")
 *  - noIndex:   true para <meta name="robots" content="noindex,nofollow" />
 *  - jsonLd:    objeto o array de objetos con datos estructurados Schema.org
 */
export const SITE_URL = "https://asatta.org";
const DEFAULT_IMAGE = `${SITE_URL}/assets/brand/logo-asatta.png`;

export default function SEO({
    title,
    description,
    path = "/",
    image = DEFAULT_IMAGE,
    type = "website",
    noIndex = false,
    jsonLd = null,
}) {
    const fullTitle = title
        ? (title.includes("ASATTA") ? title : `${title} · ASATTA`)
        : `${SITE.fullName} · ${SITE.name}`;
    const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
    const desc = description || SITE.tagline;

    const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

    return (
        <Helmet prioritizeSeoTags>
            <html lang="es" />
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            <link rel="canonical" href={canonical} />
            {noIndex && <meta name="robots" content="noindex,nofollow" />}
            {!noIndex && <meta name="robots" content="index,follow,max-image-preview:large" />}

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={SITE.name} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={image} />
            <meta property="og:locale" content="es_ES" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={image} />

            {/* JSON-LD Schema.org */}
            {jsonLdArray.map((data, idx) => (
                <script
                    key={idx}
                    type="application/ld+json"
                >
                    {JSON.stringify(data)}
                </script>
            ))}
        </Helmet>
    );
}

/* -------------------- Schema.org helpers -------------------- */

export const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE.fullName,
    "alternateName": "ASATTA",
    "url": SITE_URL,
    "logo": `${SITE_URL}/assets/brand/logo-asatta.png`,
    "email": SITE.email,
    "address": {
        "@type": "PostalAddress",
        "streetAddress": "Batalla del Salado 36",
        "postalCode": "11380",
        "addressLocality": "Tarifa",
        "addressRegion": "Cádiz",
        "addressCountry": "ES",
    },
    "taxID": "G72358849",
    "areaServed": {
        "@type": "City",
        "name": "Tarifa",
    },
    "description": SITE.tagline,
    "sameAs": [],
};

export const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE.fullName,
    "alternateName": "ASATTA",
    "url": SITE_URL,
    "inLanguage": "es-ES",
    "publisher": { "@id": `${SITE_URL}/#organization` },
};

export function breadcrumbSchema(items) {
    // items: [{name, path}]
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((it, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": it.name,
            "item": `${SITE_URL}${it.path}`,
        })),
    };
}

/**
 * Schema para un alojamiento del directorio.
 * category: hotel | hostal | apartamento | camping | otro
 */
export function lodgingSchema(member) {
    const typeMap = {
        hotel: "Hotel",
        hostal: "Hostel",
        apartamento: "Apartment",
        camping: "Campground",
        otro: "LodgingBusiness",
    };
    const schemaType = typeMap[member.category] || "LodgingBusiness";
    const slug = member.slug || "";

    const data = {
        "@context": "https://schema.org",
        "@type": schemaType,
        "name": member.name,
        "description": member.description || undefined,
        "image": member.logo || undefined,
        "url": `${SITE_URL}/directorio/${slug}`,
        "address": {
            "@type": "PostalAddress",
            "streetAddress": member.location || "Tarifa",
            "addressLocality": "Tarifa",
            "addressRegion": "Cádiz",
            "postalCode": "11380",
            "addressCountry": "ES",
        },
        "telephone": member.phone || undefined,
        "email": member.email || undefined,
        "sameAs": member.website ? [member.website] : undefined,
        "areaServed": { "@type": "City", "name": "Tarifa" },
    };
    // strip undefined
    Object.keys(data).forEach((k) => data[k] === undefined && delete data[k]);
    Object.keys(data.address).forEach(
        (k) => data.address[k] === undefined && delete data.address[k]
    );
    return data;
}
